import { useCallback, useEffect, useState } from "react";

/**
 * Simple vs Pro interface (the Coinbase "Advanced" switch / Gemini "Basic vs
 * ActiveTrader" pattern). Simple is the default so a beginner lands on one
 * clear answer; Pro is the full trading terminal.
 */
export type DeskMode = "simple" | "pro";

const KEY = "vp.mode";
const EVENT = "vp:mode";

function read(): DeskMode {
	try {
		return localStorage.getItem(KEY) === "pro" ? "pro" : "simple";
	} catch {
		return "simple";
	}
}

export function useDeskMode(): [DeskMode, (m: DeskMode) => void] {
	const [mode, setModeState] = useState<DeskMode>(read);

	// Keep every consumer (nav switch, desk page) in sync within the tab.
	useEffect(() => {
		const onChange = (e: Event) => setModeState((e as CustomEvent<DeskMode>).detail);
		// Another tab flipped the switch: follow it, so tabs never disagree.
		const onStorage = (e: StorageEvent) => { if (e.key === KEY) setModeState(read()); };
		window.addEventListener(EVENT, onChange);
		window.addEventListener("storage", onStorage);
		return () => {
			window.removeEventListener(EVENT, onChange);
			window.removeEventListener("storage", onStorage);
		};
	}, []);

	const setMode = useCallback((m: DeskMode) => {
		try {
			localStorage.setItem(KEY, m);
		} catch {
			// Storage unavailable: the choice lasts for this session only.
		}
		window.dispatchEvent(new CustomEvent<DeskMode>(EVENT, { detail: m }));
	}, []);

	return [mode, setMode];
}
